import React, { useState, useEffect } from 'react';
import { Brain, TrendingUp, AlertTriangle, CheckCircle, RefreshCw, Star, Calendar, User } from 'lucide-react';
import { Lead, ContactSummary } from '../types';
import { aiService } from '../services/aiService';

interface LeadAIAnalysisPanelProps {
  lead: Lead;
}

const LeadAIAnalysisPanel: React.FC<LeadAIAnalysisPanelProps> = ({ lead }) => {
  const [analysis, setAnalysis] = useState<ContactSummary | null>(null);
  const [loading, setLoading] = useState(false);

  // Rate limiting is enforced server-side in openai-proxy.

  useEffect(() => {
    generateAnalysis();
  }, [lead.id]);

  const generateAnalysis = async () => {
    setLoading(true);
    try {
      console.log('🤖 Generando análisis IA para lead:', lead.fullName);
      const leadAnalysis = await aiService.generateLeadSummary(lead);
      setAnalysis(leadAnalysis);
    } catch (error) {
      console.error('Error generating lead analysis:', error);
    } finally {
      setLoading(false);
    }
  };

  const getRiskColor = (risk: ContactSummary['riskLevel']) => {
    switch (risk) {
      case 'low': return 'text-green-600 bg-green-100';
      case 'medium': return 'text-yellow-600 bg-yellow-100';
      case 'high': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getRiskIcon = (risk: ContactSummary['riskLevel']) => {
    switch (risk) {
      case 'low': return CheckCircle;
      case 'medium': return AlertTriangle;
      case 'high': return AlertTriangle;
      default: return AlertTriangle;
    }
  };

  const getRiskLabel = (risk: ContactSummary['riskLevel']) => {
    switch (risk) {
      case 'low': return 'Bajo Riesgo';
      case 'medium': return 'Riesgo Medio';
      case 'high': return 'Alto Riesgo';
      default: return 'Sin Evaluar';
    }
  };

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'text-green-600 bg-green-100';
    if (score >= 60) return 'text-yellow-600 bg-yellow-100';
    if (score >= 40) return 'text-orange-600 bg-orange-100';
    return 'text-red-600 bg-red-100';
  };

  const getScoreLabel = (score: number) => {
    if (score >= 80) return 'Muy Alto';
    if (score >= 60) return 'Alto';
    if (score >= 40) return 'Medio';
    return 'Bajo';
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mr-3"></div>
          <p className="text-gray-600">Analizando lead con IA...</p>
        </div>
      </div>
    );
  }

  if (!analysis) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="text-center py-8">
          <Brain className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500 mb-4">No hay análisis disponible</p>
          <button
            onClick={generateAnalysis}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
          >
            Generar Análisis IA
          </button>
        </div>
      </div>
    );
  }

  const RiskIcon = getRiskIcon(analysis.riskLevel);

  return (
    <div className="space-y-6">
      {/* Lead Info Card */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center space-x-4 mb-4">
          <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
            <User className="w-6 h-6 text-blue-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{lead.fullName}</h3>
            <p className="text-sm text-gray-600">{lead.company}</p>
          </div>
        </div>
        
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <p className="text-gray-500">Fuente</p>
            <p className="font-medium text-gray-900">{lead.source}</p>
          </div>
          <div>
            <p className="text-gray-500">Estado</p>
            <p className="font-medium text-gray-900">{lead.status}</p>
          </div>
          <div>
            <p className="text-gray-500">Puntuación</p>
            <div className={`inline-flex items-center space-x-1 px-2 py-1 rounded-full ${getScoreColor(lead.score)}`}>
              <Star className="w-3 h-3" />
              <span className="text-xs font-medium">{lead.score}/100</span>
            </div>
          </div>
          <div>
            <p className="text-gray-500">Creado</p>
            <p className="font-medium text-gray-900">{lead.createdAt.toLocaleDateString()}</p>
          </div>
        </div>
      </div>

      {/* AI Analysis */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Brain className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Análisis IA</h3>
                <p className="text-sm text-gray-500">
                  Actualizado: {analysis.lastUpdated.toLocaleString()}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <div className={`flex items-center space-x-2 px-3 py-1 rounded-full ${getRiskColor(analysis.riskLevel)}`}>
                <RiskIcon className="w-4 h-4" />
                <span className="text-sm font-medium">{getRiskLabel(analysis.riskLevel)}</span>
              </div>
              <button
                onClick={generateAnalysis}
                disabled={loading}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
                title="Actualizar análisis"
              >
                <RefreshCw className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Summary Text */}
          <div>
            <h4 className="text-sm font-medium text-gray-900 mb-2">Resumen del Lead</h4>
            <p className="text-gray-700 leading-relaxed">{analysis.summary}</p>
          </div>

          {/* Key Points */}
          <div>
            <h4 className="text-sm font-medium text-gray-900 mb-3">Puntos Clave</h4>
            <div className="space-y-2">
              {analysis.keyPoints.map((point, index) => (
                <div key={index} className="flex items-start space-x-2">
                  <div className="w-1.5 h-1.5 bg-purple-600 rounded-full mt-2 flex-shrink-0"></div>
                  <p className="text-sm text-gray-700">{point}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Next Steps */}
          <div>
            <h4 className="text-sm font-medium text-gray-900 mb-3">Próximos Pasos Sugeridos</h4>
            <div className="space-y-2">
              {analysis.nextSteps.map((step, index) => (
                <div key={index} className="flex items-start space-x-2">
                  <div className="w-5 h-5 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-xs font-medium mt-0.5 flex-shrink-0">
                    {index + 1}
                  </div>
                  <p className="text-sm text-gray-700">{step}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Risk Analysis */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="text-sm font-medium text-gray-900 mb-2">Análisis de Riesgo</h4>
            <div className="flex items-center space-x-2">
              <RiskIcon className={`w-4 h-4 ${analysis.riskLevel === 'low' ? 'text-green-600' : analysis.riskLevel === 'medium' ? 'text-yellow-600' : 'text-red-600'}`} />
              <span className="text-sm text-gray-700">
                {analysis.riskLevel === 'low' && 'Lead con alta probabilidad de conversión. Mantener seguimiento regular.'}
                {analysis.riskLevel === 'medium' && 'Lead requiere atención moderada. Considerar aumentar frecuencia de contacto.'}
                {analysis.riskLevel === 'high' && 'Lead en riesgo de pérdida. Acción inmediata requerida para evitar que se descarte.'}
              </span>
            </div>
          </div>

          {/* Lead Specific Insights */}
          <div className="bg-blue-50 rounded-lg p-4">
            <h4 className="text-sm font-medium text-blue-900 mb-2">Insights Específicos</h4>
            <div className="space-y-2 text-sm text-blue-700">
              <div className="flex items-center space-x-2">
                <Star className="w-4 h-4" />
                <span>Puntuación actual: {lead.score}/100 ({getScoreLabel(lead.score)} interés)</span>
              </div>
              <div className="flex items-center space-x-2">
                <Calendar className="w-4 h-4" />
                <span>
                  {lead.nextFollowUp 
                    ? `Próximo seguimiento: ${lead.nextFollowUp.toLocaleDateString()}`
                    : 'Sin seguimiento programado'
                  }
                </span>
              </div>
              <div className="flex items-center space-x-2">
                <User className="w-4 h-4" />
                <span>Responsable: {lead.owner}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LeadAIAnalysisPanel;